import axios from 'axios'
import Cookies from "universal-cookie"

const API_BASE = process.env.REACT_APP_API_BASE_URL

function getToken() {
    const cookies = new Cookies();
    const token = cookies.get("token");
    
    // Note: Laravel Sanctum tokens are not JWTs, so we skip format validation
    // Basic XSS protection only
    if (token && (token.includes('<script') || token.includes('javascript:'))) {
        console.warn('Suspicious token content detected, removing token');
        return null;
    }
    
    return token;
}

// Helper function to get authorization headers
const getAuthHeaders = () => {
    const token = getToken()
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
    }
}

/**
 * Get all research methodologies for a project
 */
export const getResearchMethodologies = async (projectUuid) => {
    try {
        const response = await axios.get(`${API_BASE}/project/${projectUuid}/research-methodologies`, {
            headers: getAuthHeaders()
        })
        return response.data
    } catch (error) {
        console.error('Error fetching research methodologies:', error)
        throw {
            error: error.response?.data?.error || 'Failed to fetch research methodologies',
            status: error.response?.status || 500
        }
    }
}

/**
 * Create a new research methodology
 */
export const createResearchMethodology = async (projectUuid, methodologyData) => {
    try {
        const response = await axios.post(`${API_BASE}/project/${projectUuid}/research-methodologies`, methodologyData, {
            headers: getAuthHeaders()
        })
        return response.data
    } catch (error) {
        console.error('Error creating research methodology:', error)
        throw {
            error: error.response?.data?.error || 'Failed to create research methodology',
            status: error.response?.status || 500
        }
    }
}

/**
 * Update a research methodology
 */
export const updateResearchMethodology = async (projectUuid, methodologyUuid, methodologyData) => {
    try {
        const response = await axios.put(`${API_BASE}/project/${projectUuid}/research-methodologies/${methodologyUuid}`, methodologyData, {
            headers: getAuthHeaders()
        })
        return response.data
    } catch (error) {
        console.error('Error updating research methodology:', error)
        throw {
            error: error.response?.data?.error || 'Failed to update research methodology',
            status: error.response?.status || 500
        }
    }
}

/**
 * Delete a research methodology
 */
export const deleteResearchMethodology = async (projectUuid, methodologyUuid) => {
    try {
        const response = await axios.delete(`${API_BASE}/project/${projectUuid}/research-methodologies/${methodologyUuid}`, {
            headers: getAuthHeaders()
        })
        return response.data
    } catch (error) {
        console.error('Error deleting research methodology:', error)
        throw {
            error: error.response?.data?.error || 'Failed to delete research methodology',
            status: error.response?.status || 500
        }
    }
}

/**
 * Generate AI suggestions for methodology based on framework questions
 */
export const generateMethodologyAISuggestions = async (projectUuid, formResponses, language = 'en') => {
    try {
        const response = await axios.post(`${API_BASE}/project/${projectUuid}/research-methodologies/generate-ai`, {
            form_responses: formResponses,
            language: language
        }, {
            headers: getAuthHeaders(),
            timeout: 60000 // 60 second timeout for AI generation
        })
        return response.data
    } catch (error) {
        console.error('Error generating AI methodology suggestions:', error)
        
        // Handle timeout errors specifically
        if (error.code === 'ECONNABORTED') {
            throw {
                error: 'Request timeout. AI generation is taking longer than expected. Please try again.',
                status: 408
            }
        }
        
        throw {
            error: error.response?.data?.error || 'Failed to generate AI methodology suggestions',
            status: error.response?.status || 500
        }
    }
}

/**
 * Select a specific methodology (set as active)
 */
export const selectResearchMethodology = async (projectUuid, methodologyUuid) => {
    try {
        const response = await axios.post(`${API_BASE}/project/${projectUuid}/research-methodologies/${methodologyUuid}/select`, {}, {
            headers: getAuthHeaders()
        })
        return response.data
    } catch (error) {
        console.error('Error selecting research methodology:', error)
        throw {
            error: error.response?.data?.error || 'Failed to select research methodology',
            status: error.response?.status || 500
        }
    }
}